import { z } from "zod";

/** Legacy channel defaults, preserved for existing prompts and callers. */
export const promptCatalog = {
  "outbound.email": {
    title: "Outbound · Email",
    description:
      "Describe the sender, your product, tone, calls to action and anything the email must include or avoid.",
  },
  "outbound.linkedin": {
    title: "Outbound · LinkedIn",
    description:
      "LinkedIn connection notes and InMails are short; say how formal to be and what to ask for.",
  },
  "outbound.twitter": {
    title: "Outbound · X DM",
    description: "DMs are casual and brief; say what voice to use and what the DM should lead to.",
  },
} as const;
export type PromptKey = keyof typeof promptCatalog;
export const promptKeys = Object.keys(promptCatalog) as [PromptKey, ...PromptKey[]];
export const promptKeySchema = z.string().trim().min(1);
export const promptCreateSchema = z
  .object({
    title: z.string().trim().min(1),
    description: z.string().trim().default(""),
    body: z.string().trim().max(20_000),
  })
  .strict();
export const promptInputSchema = promptCreateSchema
  .partial({ title: true })
  .extend({ description: z.string().trim().optional() });
export type PromptCreate = z.infer<typeof promptCreateSchema>;
export type PromptInput = z.infer<typeof promptInputSchema>;
export const promptSchema = z.object({
  key: promptKeySchema,
  title: z.string(),
  description: z.string(),
  body: z.string(),
  updatedAt: z.string().nullable(),
  updatedByName: z.string().nullable(),
});
export type Prompt = z.infer<typeof promptSchema>;
export const promptsSchema = z.object({ items: z.array(promptSchema) });

/**
 * Questions and answers. A question is usually lifted from a prospect's reply;
 * the answer is written once and kept so it can be reused the next time.
 */
export const QA_FIELD_LIMIT = 20_000;
export const qaEntryInputSchema = z
  .object({
    question: z.string().trim().min(1).max(QA_FIELD_LIMIT),
    answer: z.string().trim().min(1).max(QA_FIELD_LIMIT),
    source: z.string().trim().max(QA_FIELD_LIMIT).default(""),
  })
  .strict();
export type QaEntryInput = z.infer<typeof qaEntryInputSchema>;
export const qaEntrySchema = z.object({
  id: z.string(),
  question: z.string(),
  answer: z.string(),
  source: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  updatedByName: z.string().nullable(),
});
export type QaEntry = z.infer<typeof qaEntrySchema>;
export const qaEntriesSchema = z.object({ items: z.array(qaEntrySchema) });
